import { useEffect, useMemo, useState } from "react"
import { InspectorPanelKeyValueDropdown } from "../InspectorPanelKeyValueDropdown"
import {
    IconShape,
    IconPackOptionLabels,
    IconPackOption,
    hexColorSwatches,
    ColorSwatchCategory,
    ImageProcessingState,
    maxIconImagesCount,
    LineWidth,
} from "@nova/constants"
import { ScrollableArea } from "@nova/component-scrollable-area"
import classNames from "classnames"
import { InspectorPanelSettingsFields } from "../InspectorPanelSettingsFields"
import * as Icons from "@nova/component-icons"
import { decodeNumberToColor, encodeColorToNumber } from "@nova/serializer/src/client/util"
import { iconByShape, iconsByIconPack, shapeIcons } from "./constants"
import { ColorPalette } from "./TODO-put-in-package/ColorPalette"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { iconShapeOptionFromUrl } from "./module"
import { updateIcon } from "features/pubsub/entityActions/cubes/icon"
import {
    selectIconNumberStartAt,
    selectIconTemplate,
    setActiveTool,
    updateIconTemplate,
} from "plugins/Toolbox/tool-registry"
import { InspectorPanelIconPackDropdown } from "./icon-pack-dropdown/InspectorPanelIconPackDropdown"
import {
    OptimisticLocalStateParams,
    useDefinedOptimisticLocalState,
} from "../useOptimisticLocalState"
import {
    selectIconById,
    selectIconColorById,
    selectIconOutlineById,
    selectIconPackById,
    selectIconShapeById,
    selectIconWidthById,
} from "features/entity-caching/icons/selectors"
import { Selector } from "react-redux"
import { RootState } from "integrations/redux"
import { useUploadFlowContext } from "panels/content/side-panel/upload-flow/useUploadFlowContext"
import { AddMoreDropBox } from "panels/content/top-bar/TODO-put-in-package/dropbox/AddMoreDropBox"
import { ImageGrid, RenderUploadedIconImage } from "./InspectorPanelIconUpload"
import {
    deleteSelectedIconImages,
    markIconImageUploadAsNotExceededMaxUploads,
    selectHasUploadExceededMax,
    selectIsUploadMarkedAsCancelled,
    selectSelectedIconImages,
    showUploadPageForIcon,
} from "plugins/Toolbox/Icons/IconSlice"
import { SidePanelProgressBar } from "@nova/component-side-panel-tab-container"
import { useUploadSpeedForFlightId } from "@nova/slice-multifile-upload"
import { ButtonBorderless, ButtonDestructive, ButtonPrimary } from "@nova/component-btn"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { AlertContainer } from "@nova/component-alert-container"
import { InspectorPanelIconNumberMenu } from "./InspectorPanelIconNumberMenu"

const iconColorSwatches = hexColorSwatches[ColorSwatchCategory.Icon].map((hex) =>
    encodeColorToNumber(hex)
)

export const numberIconColorSwatches = hexColorSwatches[ColorSwatchCategory.NumberIcon].map((hex) =>
    encodeColorToNumber(hex)
)

function useInspectorPanelIconLayer({
    colorParams,
    shapeParams,
    iconPackParams,
    outlineParams,
    widthParams,
    sizeParams,
}: {
    colorParams: OptimisticLocalStateParams<number>
    shapeParams: OptimisticLocalStateParams<IconShape>
    iconPackParams: OptimisticLocalStateParams<IconPackOption>
    outlineParams: OptimisticLocalStateParams<number>
    widthParams: OptimisticLocalStateParams<number>
    sizeParams: OptimisticLocalStateParams<number>
}) {
    const [openDropdownId, setOpenDropdownId] = useState<string>()
    const [color, setColor] = useDefinedOptimisticLocalState(colorParams)
    const [shape, setShape] = useDefinedOptimisticLocalState(shapeParams)
    const [iconPack, setIconPack] = useDefinedOptimisticLocalState(iconPackParams)
    const [outline, setOutline] = useDefinedOptimisticLocalState(outlineParams)
    const [width, setWidth] = useDefinedOptimisticLocalState(widthParams)
    const [size, setSize] = useDefinedOptimisticLocalState(sizeParams)
    return {
        openDropdownId,
        setOpenDropdownId,
        color,
        setColor,
        shape,
        setShape,
        iconPack,
        setIconPack,
        outline,
        setOutline,
        width,
        setWidth,
        size,
        setSize,
    }
}

export const InspectorPanelIconLayer = ({
    id,
    shouldShowTopBorder = true,
}: {
    id?: string
    shouldShowTopBorder?: boolean
}) => {
    const {
        supportedFileUploadExtensions,
        stageFiles,
        portfolioId,
        fireId,
        flightId,
        selectUploadIdsForSet,
    } = useUploadFlowContext()
    const dispatch = useAppDispatch()
    const icon = useAppSelector(selectIconById(id || ""))
    const iconHasExceededMaxUploads = useAppSelector(selectHasUploadExceededMax(id || ""))
    const iconTemplate = useAppSelector(selectIconTemplate)
    const selectedIconImageIdsMap = useAppSelector(selectSelectedIconImages(id || ""))
    const selectedImageCount = useMemo(
        () =>
            Object.values(selectedIconImageIdsMap).reduce(
                (count, isSelected) => (isSelected ? count + 1 : count),
                0
            ),
        [selectedIconImageIdsMap]
    )
    const confirmationModal = useConfirmationModalContext()
    const uploadIds = useAppSelector(!!id ? selectUploadIdsForSet(flightId) : () => [])
    const numberStartAt = useAppSelector(selectIconNumberStartAt())

    const hookParams: Parameters<typeof useInspectorPanelIconLayer>[0] = useMemo(
        () =>
            !!id
                ? {
                      colorParams: {
                          selector: selectIconColorById(id) as Selector<RootState, number>,
                          updateFn: (color) => {
                              dispatch(updateIcon({ id: icon.id, updates: { color } }))
                              dispatch(updateIconTemplate({ color }))
                          },
                          defaultValue: icon.color || iconColorSwatches[0],
                      },
                      outlineParams: {
                          selector: selectIconOutlineById(id) as Selector<RootState, number>,
                          updateFn: (outline) => {
                              dispatch(updateIcon({ id: icon.id, updates: { outline } }))
                              dispatch(updateIconTemplate({ outline }))
                          },
                          defaultValue: icon.outline || numberIconColorSwatches[0],
                      },
                      widthParams: {
                          selector: selectIconWidthById(id) as Selector<RootState, number>,
                          updateFn: (width) => {
                              dispatch(updateIcon({ id: icon.id, updates: { width } }))
                              dispatch(updateIconTemplate({ width }))
                          },
                          defaultValue: icon.width || LineWidth.none,
                      },
                      sizeParams: {
                          selector: selectIconWidthById(id) as Selector<RootState, number>,
                          updateFn: (size) => {
                              dispatch(updateIcon({ id: icon.id, updates: { size } }))
                              dispatch(updateIconTemplate({ size }))
                          },
                          defaultValue: icon.size,
                      },
                      shapeParams: {
                          selector: selectIconShapeById(id) as Selector<RootState, IconShape>,
                          updateFn: (shape) => {
                              dispatch(updateIcon({ id: icon.id, updates: { url: shape } }))
                              dispatch(updateIconTemplate({ url: shape, name: "Point" }))
                          },
                          defaultValue:
                              (!!icon.url?.startsWith("shape") && (icon.url as IconShape)) ||
                              IconShape.Circle,
                      },
                      iconPackParams: {
                          selector: selectIconPackById(id) as Selector<RootState, IconPackOption>,
                          updateFn: (iconPack) => {
                              // If user switched to shape icon pack, change icon to first shape.
                              if (iconPack === IconPackOption.Shape) {
                                  dispatch(
                                      updateIcon({
                                          id,
                                          updates: {
                                              url: IconShape.Circle,
                                              color: icon.color || iconColorSwatches[0],
                                          },
                                      })
                                  )
                                  dispatch(
                                      updateIconTemplate({
                                          pack: iconPack,
                                          url: IconShape.Circle,
                                          color: icon.color || iconColorSwatches[0],
                                          name: "Point",
                                      })
                                  )
                              }
                              // If user switched to any other icon pack, change icon to firs icon in the pack.
                              else {
                                  const pack = iconsByIconPack[iconPack]
                                  const firstCategoryInPack = Object.values(pack)[0]
                                  const firstIconInCategory = Object.values(firstCategoryInPack)[0]
                                  dispatch(
                                      updateIcon({
                                          id: icon.id,
                                          updates: {
                                              url: firstIconInCategory.url,
                                              name: firstIconInCategory.name,
                                          },
                                      })
                                  )
                                  dispatch(
                                      updateIconTemplate({
                                          pack: iconPack,
                                          url: firstIconInCategory.url,
                                          name: firstIconInCategory.name,
                                      })
                                  )
                              }
                          },
                          defaultValue: iconShapeOptionFromUrl(icon?.url),
                      },
                  }
                : {
                      colorParams: {
                          selector: () => iconTemplate.color,
                          updateFn: (color) => {
                              dispatch(updateIconTemplate({ color }))
                          },
                          defaultValue: iconTemplate.color,
                      },
                      outlineParams: {
                          selector: () => iconTemplate.outline,
                          updateFn: (outline) => {
                              dispatch(updateIconTemplate({ outline }))
                          },
                          defaultValue: iconTemplate.outline,
                      },
                      widthParams: {
                          selector: () => iconTemplate.width,
                          updateFn: (width) => {
                              dispatch(updateIconTemplate({ width }))
                          },
                          defaultValue: iconTemplate.width,
                      },
                      sizeParams: {
                          selector: () => iconTemplate.size,
                          updateFn: (size) => {
                              dispatch(updateIconTemplate({ size }))
                          },
                          defaultValue: iconTemplate.size,
                      },
                      shapeParams: {
                          selector: () => iconTemplate.url as IconShape,
                          updateFn: (shape) => {
                              dispatch(updateIconTemplate({ url: shape, name: "Point" }))
                          },
                          defaultValue: iconTemplate.url as IconShape,
                      },
                      iconPackParams: {
                          selector: () => iconTemplate.pack,
                          updateFn: (iconPack) => {
                              // If user switched to shape icon pack, change icon to first shape.
                              if (iconPack === IconPackOption.Shape) {
                                  dispatch(
                                      updateIconTemplate({
                                          pack: iconPack,
                                          url: IconShape.Circle,
                                          color: iconColorSwatches[0],
                                          name: "Point",
                                      })
                                  )
                              } else if (iconPack === IconPackOption.Number) {
                                  dispatch(
                                      updateIconTemplate({
                                          pack: iconPack,
                                          url: `numbers/${numberStartAt}`,
                                          color: numberIconColorSwatches[0],
                                          name: numberStartAt.toString(),
                                      })
                                  )
                              } else {
                                  const pack = iconsByIconPack[iconPack]
                                  const firstCategoryInPack = Object.values(pack)[0]
                                  const firstIconInCategory = Object.values(firstCategoryInPack)[0]
                                  dispatch(
                                      updateIconTemplate({
                                          pack: iconPack,
                                          url: firstIconInCategory.url,
                                          name: firstIconInCategory.name,
                                      })
                                  )
                              }
                          },
                          defaultValue: iconTemplate.pack,
                      },
                  },
        [id, icon, iconTemplate, dispatch, numberStartAt]
    )

    const {
        openDropdownId,
        setOpenDropdownId,
        color,
        setColor,
        outline,
        setOutline,
        width,
        setWidth,
        shape,
        setShape,
        iconPack,
        setIconPack,
    } = useInspectorPanelIconLayer(hookParams)

    const iconUrl = !!id ? icon.url : iconTemplate.url
    useEffect(() => {
        if (iconUrl.startsWith("shape") && !iconByShape[shape]) {
            setShape(iconUrl as IconShape)
        }
    }, [icon?.url, iconTemplate.url, iconUrl, id, setShape, shape])

    return (
        <>
            <InspectorPanelSettingsFields shouldShowTopBorder={shouldShowTopBorder}>
                {/* Icon Pack */}
                <InspectorPanelKeyValueDropdown
                    id="icon-pack"
                    isOpen={openDropdownId === "icon-pack"}
                    setOpenDropdownId={setOpenDropdownId}
                    keyTitle="Icon Pack"
                    dropdownTitle={iconPack}
                    align="right"
                >
                    <div className="p-1 bg-gray-700 rounded-xl shadow-card">
                        <ScrollableArea className="h-48">
                            <div className="pr-1 flex flex-col w-max">
                                {IconPackOptionLabels.map((iconPackOption, i) => (
                                    <div
                                        key={i}
                                        className={classNames(
                                            "px-2 h-8 hover:bg-gray-500 text-gray-0 flex items-center rounded-lg cursor-pointer select-none",
                                            {
                                                "bg-gray-500": iconPack === iconPackOption,
                                            }
                                        )}
                                        onClick={() => {
                                            setIconPack(iconPackOption)
                                            setOpenDropdownId(undefined)
                                        }}
                                    >
                                        <h5
                                            style={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 1,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {iconPackOption}
                                        </h5>
                                    </div>
                                ))}
                            </div>
                        </ScrollableArea>
                    </div>
                </InspectorPanelKeyValueDropdown>

                {iconPack === IconPackOption.Shape ? (
                    <>
                        {/* Shape */}
                        <InspectorPanelKeyValueDropdown
                            id="shape"
                            isOpen={openDropdownId === "shape"}
                            setOpenDropdownId={setOpenDropdownId}
                            keyTitle="Shape"
                            Icon={
                                shape.startsWith("shape/")
                                    ? iconByShape[shape]
                                    : iconByShape[iconUrl as IconShape]
                            }
                            iconAccentColor={decodeNumberToColor(color)}
                            iconStyle={{ color: decodeNumberToColor(color) }}
                        >
                            <div className="rounded-xl flex gap-1 p-1.5 bg-gray-700 shadow-card">
                                {shapeIcons.map(([shapeOption, Icon], i) => (
                                    <div
                                        key={i}
                                        className={classNames(
                                            "relative rounded-[0.4375rem] border",
                                            {
                                                "border-gray-0": shape === shapeOption,
                                                "border-transparent": shape !== shapeOption,
                                            }
                                        )}
                                        onClick={() => {
                                            setShape(shapeOption)
                                            setOpenDropdownId(undefined)
                                        }}
                                    >
                                        <Icons.Icon
                                            Icon={Icon}
                                            style={{ color: decodeNumberToColor(color) }}
                                            className="hover:brightness-125 cursor-pointer"
                                            accentColor={decodeNumberToColor(color)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </InspectorPanelKeyValueDropdown>

                        {/* Color */}
                        <InspectorPanelKeyValueDropdown
                            id="color"
                            isOpen={openDropdownId === "color"}
                            setOpenDropdownId={setOpenDropdownId}
                            keyTitle="Color"
                            Icon={Icons.BucketColor}
                            iconClasses="text-gray-0"
                            iconAccentColor={decodeNumberToColor(color)}
                        >
                            <ColorPalette
                                swatches={iconColorSwatches}
                                selectedColor={color}
                                setSelectedColor={setColor}
                                setOpenDropdownId={setOpenDropdownId}
                                allowColorPicker
                            />
                        </InspectorPanelKeyValueDropdown>
                    </>
                ) : iconPack === IconPackOption.Number ? (
                    <InspectorPanelIconNumberMenu
                        color={color}
                        setColor={setColor}
                        width={width}
                        setWidth={setWidth}
                        outline={outline}
                        setOutline={setOutline}
                        id={id}
                    />
                ) : (
                    <InspectorPanelKeyValueDropdown
                        id="icon"
                        isOpen={openDropdownId === "icon"}
                        setOpenDropdownId={setOpenDropdownId}
                        keyTitle="Icon"
                        iconUrl={iconUrl}
                        // These will be used if the iconUrl begins with "shape/". Otherwise, the shape will appear black
                        Icon={iconUrl.startsWith("shape") ? iconByShape[shape] : undefined}
                        iconStyle={{ color: decodeNumberToColor(color) }}
                    >
                        <InspectorPanelIconPackDropdown
                            selectedIconPack={iconPack}
                            iconId={id}
                            iconUrl={iconUrl}
                        />
                    </InspectorPanelKeyValueDropdown>
                )}
            </InspectorPanelSettingsFields>
            {!id && iconUrl.startsWith("numbers") && (
                <>
                    <hr className=" border-gray-700" />
                    <ButtonPrimary
                        onClick={() => dispatch(setActiveTool(undefined))}
                        title="Done"
                        className="ml-auto mr-2 my-2"
                    />
                </>
            )}
            {!!id && !!icon && (
                <>
                    <div className="mb-2 flex flex-col gap-2 shrink min-h-0">
                        {!!uploadIds.length && (
                            <div
                                className="cursor-pointer px-4"
                                onClick={() => dispatch(showUploadPageForIcon({ id }))}
                            >
                                <UploadProgressIndicator className="my-2" />
                            </div>
                        )}
                        {!!icon.images.length && (
                            <ScrollableArea shouldPreventScrollbarShifting>
                                <div className="px-4">
                                    <ImageGrid
                                        entityId={id}
                                        imageIds={icon.images.map((image) => image.id)}
                                        RenderImage={RenderUploadedIconImage}
                                        allowClick
                                        allowSelect
                                    />
                                </div>
                            </ScrollableArea>
                        )}
                        {selectedImageCount > 0 && (
                            <div className="px-4 w-full">
                                <ButtonDestructive
                                    title={
                                        "Remove Selected Image" +
                                        (selectedImageCount > 1 ? "s" : "")
                                    }
                                    onClick={async () => {
                                        const didConfirm = await confirmationModal.confirm({
                                            RenderComponent: getGenericConfirmationModalComponent({
                                                title: "Delete Selection?",
                                                message: `This will delete ${selectedImageCount} image${selectedImageCount > 1 ? "s" : ""}, do you want to proceed?`,
                                            }),
                                        })
                                        if (didConfirm) {
                                            dispatch(deleteSelectedIconImages({ iconId: id }))
                                        }
                                    }}
                                    LeftIcon={Icons.ClearAll}
                                    className="w-full"
                                />
                            </div>
                        )}
                        {icon.images.length < maxIconImagesCount && (
                            <div className="px-4">
                                <AddMoreDropBox
                                    canAcceptMultiple
                                    allowedFileExtensions={supportedFileUploadExtensions}
                                    onNewItems={(files) =>
                                        dispatch(stageFiles(portfolioId, fireId, flightId, files))
                                    }
                                    isProcessing={false}
                                    className="w-full"
                                    title="Add Images"
                                    iconOverride={Icons.AddImage}
                                />
                            </div>
                        )}
                        {iconHasExceededMaxUploads && (
                            <div className="px-4">
                                <AlertContainer
                                    type="error"
                                    title="Max Images Reached"
                                    body={`Up to ${maxIconImagesCount} images are allowed.`}
                                    onClose={() =>
                                        dispatch(
                                            markIconImageUploadAsNotExceededMaxUploads({
                                                iconId: id,
                                            })
                                        )
                                    }
                                    closeButtonOverride={
                                        <ButtonBorderless
                                            RightIcon={Icons.XClose}
                                            height="1.25rem"
                                            onClick={() => {
                                                dispatch(
                                                    markIconImageUploadAsNotExceededMaxUploads({
                                                        iconId: id,
                                                    })
                                                )
                                            }}
                                            className="!min-w-[unset] !h-[unset] !p-0"
                                        />
                                    }
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
        </>
    )
}

export function UploadProgressIndicator({ className }: { className?: string }) {
    const { selectUploadStateSummary, flightId } = useUploadFlowContext()
    const uploadStateSummary = useAppSelector(selectUploadStateSummary(flightId))
    const doneUploadingCount = useMemo(
        () => uploadStateSummary.stateCount[ImageProcessingState.UploadDone],
        [uploadStateSummary.stateCount]
    )
    const uploadingCount = useMemo(
        () => uploadStateSummary.stateCount[ImageProcessingState.Uploading],
        [uploadStateSummary.stateCount]
    )
    const processingCount = useMemo(
        () => uploadStateSummary.stateCount[ImageProcessingState.Processing],
        [uploadStateSummary.stateCount]
    )
    const uploadsCount = useMemo(
        () => uploadStateSummary.uploadCount,
        [uploadStateSummary.uploadCount]
    )
    const isUploadMarkedAsCancelled = useAppSelector(selectIsUploadMarkedAsCancelled(flightId))
    const uploadStatusText = useMemo(() => {
        if (isUploadMarkedAsCancelled) {
            return "Cancelling Upload"
        }
        if (doneUploadingCount === uploadsCount) {
            return "Upload Complete"
        }
        if (uploadingCount > 0) {
            return "Uploading"
        }
        if (processingCount > 0) {
            return "Processing"
        }
        return "Preparing to Upload"
    }, [
        isUploadMarkedAsCancelled,
        doneUploadingCount,
        uploadsCount,
        uploadingCount,
        processingCount,
    ])
    const averageUploadSpeed = useUploadSpeedForFlightId(flightId)
    return (
        <div className={classNames("flex flex-col", className)}>
            <div className="flex items-center justify-between">
                {doneUploadingCount === uploadsCount ? (
                    <h5 className="text-gray-0">{uploadStatusText}</h5>
                ) : uploadingCount > 0 ? (
                    <>
                        <div className="flex items-center gap-1 wrap">
                            <h5 className="text-gray-0">{uploadStatusText}</h5>
                            <h5 className="text-gray-100">
                                {`${doneUploadingCount || 0} / ${uploadsCount}`}
                            </h5>
                        </div>
                        <h5 className="text-gray-100">{averageUploadSpeed} Mbps</h5>
                    </>
                ) : (
                    <h5 className="text-gray-0">{uploadStatusText}</h5>
                )}
            </div>
            {doneUploadingCount !== uploadsCount && uploadingCount > 0 && (
                <SidePanelProgressBar
                    total={uploadsCount}
                    progress={doneUploadingCount || 0}
                    colorHex="#16C949" // green-500
                />
            )}
        </div>
    )
}
